import { forwardRef, useState, useEffect, ChangeEvent, ForwardedRef } from 'react';
import { useTheme } from 'styled-components';
import EmojiPicker from '../EmojiPicker';
import { EmojiOutlined, EmojiSolid } from '../../icons';
import IconButton from '../IconButton';
import Popover from '../Popover';
import StyledInputTextLine, { EmojiPickerContainer, WrapperContainer } from './styled/InputTextLine.styled';

interface InputTextLineProps extends React.InputHTMLAttributes<HTMLInputElement> {
  value: string;
  setValue: (event: ChangeEvent<HTMLInputElement> | string) => void;
  onChange: (event: ChangeEvent<HTMLInputElement>) => void;
  wrapperStyle?: React.CSSProperties;
  label?: string;
  error?: boolean;
  errorMessage?: string;
  styleContainer?: Record<string, unknown>;
  notTransparent?: boolean;
  disabled?: boolean;
  emojiPicker?: boolean;
  autocomplete?: string;
  emojiPickerPosition?: 'top-left' | 'top-right' | 'top-end' | 'bottom-right' | 'bottom-left' | 'bottom-end';
}

/**
 * It is an input field for inserting short text and numbers. It has multiple functions and supports multiple character types/formats.
 */
const InputTextLine = forwardRef((
  {
    value,
    setValue,
    onChange,
    wrapperStyle,
    label = '',
    error = false,
    errorMessage = '',
    styleContainer = {},
    notTransparent = false,
    disabled = false,
    emojiPicker = false,
    autocomplete = 'off',
    emojiPickerPosition = 'bottom-end',
    ...props
  }: InputTextLineProps,
  ref: ForwardedRef<HTMLInputElement>
) => {
  const [cursorPosition, setCursorPosition] = useState < number > ();
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);

  const theme = useTheme();

  const pickEmoji = (e: any, { emoji }: { emoji: string }) => {
    const inputRef = (ref as React.RefObject<HTMLInputElement>).current;
    if (!inputRef) return;

    inputRef.focus();
    const start = value.substring(0, inputRef.selectionStart || 0);
    const end = value.substring(inputRef.selectionStart || 0);
    const text = start + emoji + end;
    setValue(text);
    setCursorPosition(start.length + emoji.length);
  };

  const toggleEmojiPicker = (prev: boolean) => {
    setShowEmojiPicker(!prev);
  };

  useEffect(() => {
    const inputRef = (ref as React.RefObject<HTMLInputElement>)?.current;
    if (inputRef && inputRef.type !== 'email' && cursorPosition !== undefined) {
      inputRef.selectionEnd = cursorPosition;
    }
  }, [cursorPosition, ref]);

  return (
    <WrapperContainer
      className="input-text-line-wrapper"
      style={{
        ...wrapperStyle,
      }}
    >
      {/* @ts-ignore */}
      <StyledInputTextLine label={label} error={error} style={styleContainer} notTransparent={notTransparent} disabled={disabled}>
        <input ref={ref} className="inputTxt" {...props} value={value} onChange={onChange} autoComplete={autocomplete} id={`inputTextLine${label}`} disabled={disabled} />
        <label className="labelTxt" htmlFor={`inputTextLine${label}`}>{label}</label>
        <span className="highlight" />
        <span className="bar" />
        {error && <span className="errorMessage">{errorMessage}</span>}
      </StyledInputTextLine>
      {emojiPicker && ref && (
        <Popover
          open={showEmojiPicker}
          closePopover={() => setShowEmojiPicker(false)}
          // @ts-ignore
          style={{ zIndex: '1050', backgroundColor: theme.popoverBackground }}
        >
          <Popover.Action>
            <div>
              {showEmojiPicker ? (
                <IconButton color="warning" variant="pattern" shape="rounded" disabled={disabled} onClick={() => toggleEmojiPicker(showEmojiPicker)}>
                  <EmojiSolid />
                </IconButton>
              ) : (
                <IconButton color="primary" variant="pattern" shape="rounded" disabled={disabled} onClick={() => toggleEmojiPicker(showEmojiPicker)}>
                  <EmojiOutlined />
                </IconButton>
              )}
            </div>
          </Popover.Action>
          {/* @ts-ignore */}
          <Popover.Content direction={emojiPickerPosition} stayOpened>
            <EmojiPickerContainer>
              <EmojiPicker onEmojiClick={pickEmoji} />
            </EmojiPickerContainer>
          </Popover.Content>
        </Popover>
      )}
    </WrapperContainer>
  );
});

InputTextLine.displayName = 'InputTextLine';

export default InputTextLine;
